import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { DropdownMenuProps } from '@databases/atlaskit/DropdownMenu';
import { useFlaggingCallback } from '@databases/banners';
import { logError } from '@databases/logs';
import { SkeletonItem } from '@databases/skeleton';
import {
  DEFAULT_DATABASE_VIEW_SETTINGS,
  DatabaseViewSettings,
} from '@databases/types/database/view';
import { ViewChangeTypes } from '@databases/utils/database/view/view';
import useView from '@databases/views/useView';
import React, { FunctionComponent } from 'react';

import QuickSearch from './QuickSearch';

export interface DatabaseSearchProps {
  contentId?: string;
  value: DatabaseViewSettings;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  placement?: DropdownMenuProps['placement'];
  isFilterPickerOpen?: boolean;
  onFilterPickerOpenChange?: (isOpen: boolean) => void;
  autoFocusLastFilterValue?: boolean;
  showQuickSearch?: boolean;
}

/**
 * Renders different form fields to modify the settings of a database view.
 */
const DatabaseSearch: FunctionComponent<DatabaseSearchProps> = ({
  value = DEFAULT_DATABASE_VIEW_SETTINGS,
  showQuickSearch,
  contentId,
}) => {
  const client = useAnalytics();
  const { setViewChange } = useView();

  const sendSearchedEvent = useFlaggingCallback(
    async (search: DatabaseViewSettings['search']) => {
      if (search === '' || search === undefined) {
        // do not send event if search is empty
        return;
      }
      if (!contentId) {
        logError(
          new Error(
            'sendDatabasedSearchedEvent was not sent because contentId was not defined'
          )
        );
        return;
      }
      await client.sendDatabasedSearchedEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId]
  );

  const handleSearchChange = useFlaggingCallback(
    async (search: DatabaseViewSettings['search']) => {
      await sendSearchedEvent(search);
      setViewChange({
        type: ViewChangeTypes.VIEW_SEARCH,
        value: search,
      });
    },
    [setViewChange, sendSearchedEvent]
  );

  if (!showQuickSearch) {
    return null;
  }

  return (
    <SkeletonItem isButton>
      <QuickSearch value={value?.search} onChange={handleSearchChange} />
    </SkeletonItem>
  );
};

export default DatabaseSearch;
